import React from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { GripVertical, CheckSquare } from 'lucide-react';

function CheckboxComponent({ node, updateAttributes, getPos }) {
  const [text, setText] = React.useState(node.attrs.label || '');

  React.useEffect(() => {
    setText(node.attrs.label || '');
  }, [node.attrs.label]);

  const onChange = (e) => {
    setText(e.target.value);
  };

  const onBlur = () => {
    updateAttributes({ label: text });
  };

  const onDragStart = (e) => {
    try {
      const from = typeof getPos === 'function' ? getPos() : null;
      const payload = JSON.stringify({
        type: 'editor-drag',
        nodeType: 'checkbox',
        from,
      });
      e.dataTransfer.setData('application/x-rp-editor', payload);
      e.dataTransfer.setData('text/plain', payload);
      e.dataTransfer.effectAllowed = 'move';
      console.debug('[NodeView] checkbox dragstart', payload);
    } catch (err) {
      console.log('error', err.message);
    }
  };

  return (
    <NodeViewWrapper
      as="span"
      className="inline-flex items-center align-middle my-0.5"
      data-rp-node="checkbox"
    >
      <span
        className="
          inline-flex items-center gap-2 rounded-lg border border-slate-200
          bg-white px-2 py-1.5 shadow-sm transition-all
          hover:border-[#375AE7]/30 hover:shadow-md
        "
        draggable
        onDragStart={onDragStart}
      >
        <span
          draggable
          onDragStart={onDragStart}
          className="cursor-grab text-slate-300 hover:text-[#375AE7] transition-colors"
          aria-hidden
        >
          <GripVertical className="h-3.5 w-3.5" />
        </span>

        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
          <CheckSquare className="h-3 w-3" strokeWidth={2.25} />
        </span>

        <input
          type="checkbox"
          checked={node.attrs.checked}
          onChange={(e) => updateAttributes({ checked: e.target.checked })}
          onMouseDown={(e) => e.stopPropagation()}
          className="h-4 w-4 shrink-0 rounded border-slate-300 text-[#375AE7] focus:ring-[#375AE7]/30 cursor-pointer"
        />

        <input
          value={text}
          placeholder="Checkbox label…"
          className="
            min-w-[80px] max-w-[220px] border-0 bg-transparent
            text-sm font-medium text-slate-700 placeholder:text-slate-400
            outline-none focus:ring-0
          "
          onChange={onChange}
          onBlur={onBlur}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </span>
    </NodeViewWrapper>
  );
}

export const Checkbox = Node.create({
  name: 'checkbox',

  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      checked: { default: false },
      label: { default: '' },
      id: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'checkbox-component' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CheckboxComponent);
  },
  addCommands() {
    return {
      insertCheckbox:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              id: crypto.randomUUID(),
              checked: false,
              label: '',
            },
          });
        },
    };
  },
});
